package com.banyan.flightviewer.web.platform

import com.banyan.flightviewer.state.AircraftData
import kotlinx.browser.window

/**
 * Web Notifications API wrapper. Mirrors the dedupe logic from the desktop
 * `AlertManager`. Permission is requested on first user interaction (browsers
 * block notification prompts on page load).
 */
class WebAlertManager {

    var emergencySquawkAlertsEnabled = true
    val watchedCallsigns = mutableSetOf<String>()
    val watchedRegistrations = mutableSetOf<String>()

    private val firedAlerts = mutableSetOf<String>()
    private var permissionRequested = false

    /** Call from a click handler so the prompt isn't blocked. */
    fun ensurePermission() {
        if (permissionRequested) return
        permissionRequested = true
        val permission = js("Notification && Notification.permission") as? String
        if (permission == "default") {
            js("Notification.requestPermission()")
        }
    }

    fun check(aircraft: List<AircraftData>) {
        val currentKeys = mutableSetOf<String>()
        for (ac in aircraft) {
            if (emergencySquawkAlertsEnabled && ac.squawk in EMERGENCY_SQUAWKS) {
                val key = "squawk:${ac.callsign}:${ac.squawk}"
                currentKeys.add(key)
                if (key !in firedAlerts) {
                    firedAlerts.add(key)
                    val label = EMERGENCY_SQUAWKS[ac.squawk] ?: "Emergency"
                    notify("$label - ${ac.callsign}", "Squawk ${ac.squawk} at ${ac.altitude} ft")
                }
            }
            if (ac.callsign in watchedCallsigns) {
                val key = "callsign:${ac.callsign}"
                currentKeys.add(key)
                if (key !in firedAlerts) {
                    firedAlerts.add(key)
                    notify("Watched flight: ${ac.callsign}", "${ac.origin} \u2192 ${ac.destination}")
                }
            }
            if (ac.registration in watchedRegistrations && ac.registration.isNotBlank()) {
                val key = "reg:${ac.registration}"
                currentKeys.add(key)
                if (key !in firedAlerts) {
                    firedAlerts.add(key)
                    notify("Watched aircraft: ${ac.registration}", "${ac.callsign} at ${ac.altitude} ft")
                }
            }
        }
        firedAlerts.removeAll { it !in currentKeys }
    }

    private fun notify(title: String, body: String) {
        val supported = js("typeof Notification !== 'undefined'") as? Boolean ?: false
        if (!supported) return
        val permission = js("Notification.permission") as? String
        if (permission != "granted") return
        try {
            js("new Notification(title, { body: body, icon: '/icon.png' })")
        } catch (e: Throwable) {
            // window.console isn't in the older legacy bindings -- use the
            // top-level kotlin.js console.
            console.warn("notify failed: ${e.message}")
        }
    }

    companion object {
        val EMERGENCY_SQUAWKS = mapOf(
            "7700" to "EMERGENCY",
            "7600" to "RADIO FAILURE",
            "7500" to "HIJACK",
        )
    }
}
